import React from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import { appApi, useGetNcsCustomerQuery } from "../../../../store/appApi";
import { selectUserRole } from "../../../../store/authSlice";
import {
  Badge,
  STATUS_TONE,
  TRANSITION_TONE,
  btnGhost,
  fmtDate,
  fmtDateTime,
  auditActionLabel,
} from "./config";
import {
  transferToModemRecoveryApi,
  setModemOutcomeApi,
} from "../../../../services/netplusCustomerStatus";
import { uiPrompt, uiToast, uiAlert } from "./ui";

const toDateInput = (d) => new Date(d).toISOString().slice(0, 10);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 border-b border-gray-50 py-1.5 text-sm">
    <span className="text-gray-500">{k}</span>
    <span className="font-medium text-gray-800">{v ?? "—"}</span>
  </div>
);

const CustomerDrawer = ({ partnerCode, customerKey, onClose }) => {
  const dispatch = useDispatch();
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";
  const { data, isFetching } = useGetNcsCustomerQuery(
    { partnerCode, customerKey },
    { skip: !customerKey },
  );
  const c = data?.customer;
  const modemCases = data?.modemRecoveryCases || [];
  const hasOpenModem = modemCases.some((m) => m.isOpen);
  const transitions = data?.transitions || [];
  // Most recent time the customer went Inactive/Suspended, and — if it
  // happened — the recovery that followed it. `transitions` is sorted
  // newest-first, so the first LAPSED entry found is the most recent one.
  const lastLapse = transitions.find((t) => t.type === "LAPSED");
  const lapseIdx = lastLapse
    ? transitions.findIndex((t) => t._id === lastLapse._id)
    : -1;
  const recovery =
    lapseIdx > 0
      ? [...transitions.slice(0, lapseIdx)]
          .reverse()
          .find((t) => t.type === "RECOVERED")
      : null;
  const refresh = () =>
    dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));

  const transfer = async () => {
    const res = await uiPrompt({
      title: "Transfer to Modem Recovery",
      confirmText: "Transfer",
      fields: [
        { name: "reason", label: "Reason (optional)", type: "textarea", placeholder: "reason" },
        // Super-admin only — everyone else transfers dated to right now.
        ...(isSuperAdmin
          ? [
              {
                name: "transferredOn",
                label: "Transferred on (back-date if needed)",
                type: "date",
                initial: toDateInput(new Date()),
                max: toDateInput(new Date()),
              },
            ]
          : []),
      ],
    });
    if (res === null) return;
    try {
      await transferToModemRecoveryApi({
        partnerCode,
        customerKey,
        reason: res.reason || "",
        transferredOn: res.transferredOn || undefined,
      });
      refresh();
      uiToast({ type: "success", title: "Moved to modem recovery" });
    } catch (e) {
      uiToast({
        type: "error",
        title: "Transfer failed",
        message: e?.response?.data?.message || "Something went wrong.",
      });
    }
  };

  const setOutcome = async (id, outcome) => {
    const note = await uiPrompt({
      title: outcome === "recovered" ? "Mark recovered" : "Mark not recovered",
      label: "Note (optional)",
      placeholder: "note",
      confirmText: "Save",
    });
    if (note === null) return;
    try {
      await setModemOutcomeApi(id, { outcome, note: note || "" }, partnerCode);
      refresh();
    } catch (e) {
      const body = e?.response?.data;
      if (e?.response?.status === 409 && body?.reason === "customer_not_active") {
        refresh();
        await uiAlert({
          type: "warning",
          title: "Customer isn't active yet",
          message: `Customer is still ${body.customerStatus} on the latest sheet — can't mark as ${outcome} until the sheet confirms them Active.`,
        });
        return;
      }
      uiToast({
        type: "error",
        title: "Couldn't update case",
        message: body?.message || "Something went wrong.",
      });
    }
  };

  return (
    <Modal
      show={Boolean(customerKey)}
      onHide={onClose}
      size="lg"
      centered
      scrollable
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-base">
          {c ? `${c.name || c.userId}` : "Customer"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isFetching || !c ? (
          <div className="py-10 text-center text-gray-400">Loading…</div>
        ) : (
          <div className="space-y-5">
            <div className="grid gap-x-8 gap-y-0 sm:grid-cols-2">
              <Row k="User Id" v={c.userId} />
              <Row k="Phone" v={c.phone} />
              <Row
                k="Status"
                v={<Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge>}
              />
              <Row k="Previous status" v={c.previousStatus} />
              <Row k="Active plan" v={c.plan} />
              <Row k="Activation (last recharge)" v={fmtDate(c.activationDate)} />
              <Row k="Expiry (next deactivation)" v={fmtDate(c.expiryDate)} />
              <Row
                k="Overdue"
                v={c.daysOverdue != null ? `${c.daysOverdue} days` : "—"}
              />
              <Row
                k="Expiring in"
                v={c.expiringInDays != null ? `${c.expiringInDays} days` : "—"}
              />
              <Row k="Deactivated since" v={fmtDate(c.deactivatedSince)} />
              <Row k="On sheet" v={c.onSheet ? "Yes" : "No"} />
              <Row k="Service area" v={c.serviceArea} />
              <Row k="Last transition" v={c.lastTransition} />
              <Row
                k="Security"
                v={c.security != null ? `₹${Number(c.security).toLocaleString("en-IN")}` : "—"}
              />
            </div>
            {c.address && (
              <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                {c.address}
              </div>
            )}

            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Follow-up journey
              </div>
              {(data.journey || []).length === 0 ? (
                <div className="text-xs text-gray-400">
                  No calls, assignments or remarks logged yet.
                </div>
              ) : (
                <div className="max-h-72 space-y-2 overflow-y-auto">
                  {data.journey.map((j) => (
                    <div
                      key={j._id}
                      className="rounded-lg border border-gray-100 px-3 py-1.5 text-xs"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-gray-800">
                          {auditActionLabel(j.action)}
                          {j.actorName ? ` — ${j.actorName}` : ""}
                        </span>
                        <span className="shrink-0 text-gray-400">
                          {fmtDateTime(j.at)}
                        </span>
                      </div>
                      {(j.summary || j.meta?.assignedToName || j.meta?.note || j.meta?.outcome) && (
                        <div className="mt-0.5 text-gray-500">
                          {j.summary}
                          {j.meta?.outcome ? ` · ${j.meta.outcome}` : ""}
                          {j.meta?.note ? ` — ${j.meta.note}` : ""}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Last inactive / suspended
              </div>
              {lastLapse ? (
                <div className="text-xs text-gray-600">
                  {lastLapse.toStatus} on {fmtDate(lastLapse.toDate)}
                  {recovery
                    ? ` → reactivated ${fmtDate(recovery.toDate)}`
                    : " — not yet reactivated"}
                </div>
              ) : (
                <div className="text-xs text-gray-400">
                  No lapse / suspension on record.
                </div>
              )}
            </div>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Follow-ups
              </div>
              {(data.followups || []).length === 0 ? (
                <div className="text-xs text-gray-400">None</div>
              ) : (
                <div className="space-y-1.5">
                  {data.followups.map((f) => (
                    <div
                      key={f._id}
                      className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-1.5 text-xs"
                    >
                      <span>
                        {f.lapseBucket} · opened {fmtDate(f.openedOn)}
                        {f.closedAs ? ` · ${f.closedAs} ${fmtDate(f.closedOn)}` : ""}
                        {f.verificationStatus === "failed" && (
                          <span className="ml-1 font-medium text-rose-600">
                            — marked paid, still inactive next sheet
                          </span>
                        )}
                      </span>
                      <Badge
                        tone={
                          f.isOpen
                            ? "bg-amber-50 text-amber-700 border-amber-200"
                            : "bg-gray-100 text-gray-500 border-gray-200"
                        }
                      >
                        {f.state}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-gray-400">
                <span>Modem recovery</span>
                {!hasOpenModem && (
                  <button className={btnGhost} onClick={transfer}>
                    Transfer to modem recovery
                  </button>
                )}
              </div>
              {modemCases.length === 0 ? (
                <div className="text-xs text-gray-400">No cases</div>
              ) : (
                <div className="space-y-1.5">
                  {modemCases.map((m) => (
                    <div
                      key={m._id}
                      className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-1.5 text-xs"
                    >
                      <span>
                        transferred {fmtDate(m.transferredOn)}
                        {m.transferredBy?.name ? ` by ${m.transferredBy.name}` : ""}
                        {m.closedOn ? ` · ${m.state} ${fmtDate(m.closedOn)}` : ""}
                      </span>
                      {m.isOpen ? (
                        <span className="flex gap-1">
                          <button
                            className={btnGhost}
                            onClick={() => setOutcome(m._id, "recovered")}
                          >
                            Recovered
                          </button>
                          {/* Marking untraceable now requires picking who it's
                              reassigned to — do that from the full Modem
                              Recovery screen, not this compact widget. */}
                        </span>
                      ) : (
                        <Badge
                          tone={
                            m.state === "recovered"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-rose-50 text-rose-700 border-rose-200"
                          }
                        >
                          {m.state}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Transition timeline
              </div>
              <div className="max-h-60 space-y-1.5 overflow-y-auto">
                {(data.transitions || []).map((t) => (
                  <div
                    key={t._id}
                    className="flex items-center justify-between gap-2 border-b border-gray-50 pb-1.5 text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <Badge tone={TRANSITION_TONE[t.type]}>{t.type}</Badge>
                      <span className="text-gray-500">
                        {t.fromStatus || "—"} → {t.toStatus}
                      </span>
                      {t.recoveredVia && (
                        <span className="text-gray-400">({t.recoveredVia})</span>
                      )}
                    </div>
                    <span className="text-gray-400">{fmtDate(t.toDate)}</span>
                  </div>
                ))}
                {(data.transitions || []).length === 0 && (
                  <div className="text-xs text-gray-400">No transitions recorded.</div>
                )}
              </div>
            </div>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Snapshot history
              </div>
              <div className="max-h-48 overflow-y-auto">
                <table className="min-w-full text-xs">
                  <tbody>
                    {(data.history || []).map((h) => (
                      <tr key={h._id} className="border-b border-gray-50">
                        <td className="py-1 pr-3 text-gray-500">
                          {fmtDate(h.snapshotDate)}
                        </td>
                        <td className="py-1 pr-3">{h.status}</td>
                        <td className="py-1 pr-3 text-gray-400">
                          exp {fmtDate(h.expiryDate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CustomerDrawer;
